import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Heading, Container, Text, Button, Box, Spinner, HStack
} from "@chakra-ui/react";
import ReactMarkdown from "react-markdown";
import { useSelector } from "react-redux";
import { path } from "ramda";
import { useLandingContentQuery, useLandingSectionsQuery } from "../app/api";

const mdComponents = {
  h1: ({ children }) => (
    <Heading as="h1" textAlign="center" fontSize={{ base: 'xl', sm: '2xl' }} fontWeight="bold" mt={2} pt={4} pb={10}>
      {children}
    </Heading>
  ),
  h2: ({ children }) => (
    <Heading as="h2" textAlign="center" fontSize={{ base: '2xl', sm: '3xl' }} mt={16} mb={10}>
      {children}
    </Heading>
  ),
  p: ({ children }) => <Text textAlign="center" fontSize={{ base: 'lg', sm: 'xl' }} mt={6}>{children}</Text>,
  hr: () => <Box as="hr" borderTop="1px solid" borderColor="gray.200" my={6} />,
  em: ({ children }) => (
    <Text textAlign="center" fontSize={{ base: 'lg', sm: 'xl' }} mt={2} fontStyle="italic">
      {children}
    </Text>
  ),
};

export const LandingPage = () => {
  const lang = useSelector(path(["ui", "lang"])) || "et";
  const { data: contentData, isLoading: contentLoading } = useLandingContentQuery();
  const { data: sectionsData, isLoading: sectionsLoading } = useLandingSectionsQuery();
  const [activeSection, setActiveSection] = useState(null);

  if (contentLoading || sectionsLoading) {
    return <Container maxW="5xl" py={20} textAlign="center"><Spinner size="xl" /></Container>;
  }

  const get = (key) => {
    const row = contentData?.find(r => r.key === key);
    return lang === "en" ? (row?.value_en || row?.value_et || "") : (row?.value_et || "");
  };

  const activeSections = sectionsData?.filter(s => s.active) || [];
  const section = activeSection !== null ? activeSections.find(s => s.id === activeSection) : null;
  const getLabel = (s) => lang === "en" ? (s.label_en || s.label_et) : s.label_et;
  const getContent = (s) => lang === "en" ? (s.content_en || s.content_et) : s.content_et;

  return (
    <>
      {/* Section nav bar — top center */}
      {activeSections.length > 0 && (
        <Container maxW="5xl" pt={20}>
          <HStack justify="center" spacing={2} flexWrap="wrap">
            {activeSections.map(s => (
              <Button
                key={s.id}
                variant="ghost"
                colorScheme="teal"
                fontWeight="bold"
                fontSize="lg"
                size="lg"
                bg={activeSection === s.id ? "teal.50" : "transparent"}
                onClick={() => setActiveSection(activeSection === s.id ? null : s.id)}
              >
                {getLabel(s)}
              </Button>
            ))}
          </HStack>
        </Container>
      )}

      {section ? (
        /* Section view */
        <Container maxW="3xl" pt={10} pb={6}>
          <Button
            variant="ghost"
            colorScheme="teal"
            mb={0}
            onClick={() => setActiveSection(null)}
          >
            {get("back_button")}
          </Button>
          <ReactMarkdown components={mdComponents}>{getContent(section)}</ReactMarkdown>
        </Container>
      ) : (
        /* Main view */
        <>
          <Container maxW="5xl" py={10}>
            {/* Name */}
            <ReactMarkdown
              components={{
                p: ({ children }) => (
                  <Text textAlign="center" fontSize={{ base: '4xl', sm: '6xl' }} fontWeight="extrabold" mt={20}>
                    {children}
                  </Text>
                ),
                strong: ({ children }) => <>{children}</>,
              }}
            >
              {get("name")}
            </ReactMarkdown>

            {/* Intro */}
            <Container maxW="3xl" px={0}>
              <ReactMarkdown components={mdComponents}>{get("intro")}</ReactMarkdown>
            </Container>
          </Container>

          <Container mb={200} maxW="3xl" textAlign="center" py={10}>
            <Button as={Link} to="/app" colorScheme="teal" size="lg">
              {get("start_button")}
            </Button>
          </Container>
        </>
      )}
    </>
  );
};

export default LandingPage;